/**
 * configFerramentas
 * =================
 * Liga/desliga dos módulos opcionais ("Ferramentas") do menu lateral, POR
 * LOJA. A fonte da verdade é o banco (gesconfig_ferramenta), entregue pelo
 * /v1/configs/all na seção `ferramentas`; o ConfigsContext espelha em
 * localStorage ('configFerramentas'), que é o que este arquivo lê de forma
 * síncrona. Default DESATIVADAS, exceto as marcadas com `padrao: true`.
 *
 * Consumidas por:
 *   - pages/configuracoes/Configuracoes.js  (aba "Ferramentas" -> salvarFerramenta)
 *   - layouts/sidebars/vertical/Sidebar.js  (esconde itens desativados)
 *   - routes/FerramentaGuard.js             (bloqueia a rota)
 */

import { useState, useEffect } from 'react';
import api from '../services/api';

const STORAGE_KEY = 'configFerramentas';
export const FERRAMENTAS_EVENT = 'configFerramentasChanged';

// Metadados das 4 ferramentas opcionais. `href` casa com o item da SidebarData.
export const FERRAMENTAS = [
  {
    key: 'integracoes',
    label: 'Integrações',
    href: '/inicio/integracao',
    description: 'Hub de conectores externos (marketplaces, e-commerce, etc.)',
  },
  {
    key: 'onboarding',
    label: 'Configuração Inicial',
    href: '/inicio/onboarding',
    description: 'Assistente guiado de configuração inicial do sistema',
    // Ligada por padrão: o login manda o cliente novo para /inicio/onboarding,
    // e com a flag desligada o guard o devolvia ao dashboard.
    padrao: true,
  },
  {
    key: 'alertas',
    label: 'Alertas',
    href: '/alertas',
    description: 'Central de alertas e regras de não-conformidade',
  },
  {
    key: 'dashboardPreditivo',
    label: 'Dashboard Preditivo',
    href: '/inicio/dashboard-preditivo',
    description: 'Inteligência preditiva de vendas e estoque',
  },
];

// Default: desativadas, salvo `padrao: true`. O que o usuário gravou prevalece.
const DEFAULTS = FERRAMENTAS.reduce((acc, f) => {
  acc[f.key] = !!f.padrao;
  return acc;
}, {});

// Mapa href -> key, usado pela sidebar para filtrar os itens.
export const FERRAMENTA_HREFS = FERRAMENTAS.reduce((acc, f) => {
  acc[f.href] = f.key;
  return acc;
}, {});

export function getConfigFerramentas() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch (e) {
    return { ...DEFAULTS };
  }
}

export function setFerramenta(key, value) {
  const next = { ...getConfigFerramentas(), [key]: !!value };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event(FERRAMENTAS_EVENT));
  } catch (e) {
    /* localStorage indisponível — mantém em memória do chamador */
  }
  return next;
}

/**
 * Grava a escolha para a LOJA atual (vale em qualquer computador). Atualiza o
 * cache local na hora (menu reage sem esperar a rede) e desfaz se o servidor
 * recusar. Rejeita a promise em caso de erro, para a tela avisar.
 */
export async function salvarFerramenta(key, value) {
  const anterior = getConfigFerramentas();
  const next = setFerramenta(key, value);
  try {
    await api.put('/v1/configs/ferramentas', {
      idcliente: parseInt(localStorage.getItem('sessionCodidcliente'), 10) || 0,
      idloja: parseInt(localStorage.getItem('sessionloja'), 10) || 0,
      chave: key,
      ativo: !!value,
    });
    return next;
  } catch (e) {
    setFerramenta(key, anterior[key]);
    throw e;
  }
}

/**
 * Hook reativo: re-renderiza quando as flags mudam (mesma aba via evento
 * custom, ou outra aba via evento 'storage').
 */
export function useConfigFerramentas() {
  const [cfg, setCfg] = useState(getConfigFerramentas);
  useEffect(() => {
    const update = () => setCfg(getConfigFerramentas());
    window.addEventListener(FERRAMENTAS_EVENT, update);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener(FERRAMENTAS_EVENT, update);
      window.removeEventListener('storage', update);
    };
  }, []);
  return cfg;
}
